import { useEffect, useState } from "react";
import { useLocation } from "wouter";

import {
    Battle,
    Objective,
    Quest,
    QUEST_DIFFICULTY,
    UpdateQuest,
} from "../interfaces";
import { get, post, patch, put, _delete } from "../common/net/requester";
import { QuestView, NewQuest, PlatformHeader } from "../components";

import "../styles/questlog.css";
import { COLOR, gradient } from "../common/css/colors";

interface LogEntryProps {
    id: string;
    title: string;
    difficulty: number;
    active: boolean;
    complete?: boolean;
    onClick: () => void;
}

const LogEntry = ({
    id,
    title,
    difficulty,
    active,
    complete,
    onClick,
}: LogEntryProps) => {
    const [, setLocation] = useLocation();

    let background = "transparent";
    switch (difficulty) {
        case QUEST_DIFFICULTY.GRAY:
            background = gradient(COLOR.pink5, COLOR.blue1).background;
            break;
        case QUEST_DIFFICULTY.GREEN:
            background = gradient(COLOR.green, COLOR.lime1).background;
            break;
        case QUEST_DIFFICULTY.YELLOW:
            background = gradient(COLOR.orange, COLOR.yellow).background;
            break;
        case QUEST_DIFFICULTY.ORANGE:
            background = gradient(COLOR.orange, COLOR.red2).background;
            break;
        case QUEST_DIFFICULTY.RED:
            background = gradient(COLOR.red3, COLOR.red5).background;
            break;
    }

    return (
        <div
            className={`logEntry ${active ? "active" : ""} ${
                complete ? "complete" : ""
            }`}
            style={{
                background: background,
                color: "white",
                padding: "0.8em",
                fontWeight: "400",
                fontFamily: "sans-serif",
            }}
            onClick={() => {
                onClick();
                setLocation(`/questlog/${id}`);
            }}
        >
            {title}
        </div>
    );
};

const QuestLog = ({ sub }: { sub?: string }) => {
    const [location, setLocation] = useLocation();
    const [quests, setQuests] = useState<Array<Quest>>([]);
    const [creatingNew, setCreatingNew] = useState(false);
    const [selectedQuestId, setSelectedQuestId] = useState("");

    useEffect(() => {
        if (location.split("/").includes("add")) setCreatingNew(true);
    }, [location]);

    useEffect(() => {
        get<Array<Quest>>("/quest")
            .then((res) => {
                if (!res.ok) {
                    console.log(res.status, res.message);
                    return;
                }

                setQuests(res.body);
            })
            .catch((err) => console.log("failed to get quests:", err));
    }, []);

    useEffect(() => {
        if (quests.length === 0 || !sub || Number.isNaN(Number.parseInt(sub)))
            return;

        setSelectedQuestId(sub);
    }, [quests, sub]);

    const addQuest = (quest: Quest) => {
        setQuests((p) => [...p, quest]);
        setCreatingNew(false);
        setSelectedQuestId(quest.id);
        setLocation(`/questlog/${quest.id}`);
    };

    const updateQuest = (questId: string, updates: UpdateQuest) => {
        if (Object.entries(updates).length === 0) return;

        patch(`/quest/${questId}`, updates).then((res) => {
            if (!res.ok) {
                console.log(res.status, res.message);
                return;
            }

            setQuests((p) =>
                p.map((quest) =>
                    quest.id === questId ? { ...quest, ...updates } : quest,
                ),
            );
        });
    };

    const addObjective = (questId: string, objective: Objective) => {
        post<Objective>(`/quest/${questId}/objective`, objective)
            .then((res) => {
                if (!res.ok) {
                    console.log(res.status, res.message);
                    return;
                }

                setQuests((p) =>
                    p.map((quest) =>
                        quest.id === questId
                            ? {
                                  ...quest,
                                  objectives: [...quest.objectives, res.body],
                              }
                            : quest,
                    ),
                );
            })
            .catch((err) => console.error("error adding objective:", err));
    };

    const addBattle = (questId: string, battle: Battle) => {
        post<Battle>(`/quest/${questId}/battle`, battle)
            .then((res) => {
                if (!res.ok) {
                    console.log(res.status, res.message);
                    return;
                }

                setQuests((p) =>
                    p.map((quest) =>
                        quest.id === questId
                            ? {
                                  ...quest,
                                  battles: [...quest.battles, res.body],
                              }
                            : quest,
                    ),
                );
            })
            .catch((err) => console.error("error adding objective:", err));
    };

    const removeBattle = (questId: string, battleId: string) => {
        _delete(`/quest/${questId}/battle/${battleId}`).then((res) => {
            if (!res.ok) {
                console.log(res.status, res.message);
                return;
            }

            setQuests((p) =>
                p.map((quest) =>
                    quest.id === questId
                        ? {
                              ...quest,
                              battles: quest.battles.filter(
                                  (battle) => battle.id !== battleId,
                              ),
                          }
                        : quest,
                ),
            );
        });
    };

    const deleteQuest = (questId: string) => {
        _delete(`/quest/${questId}`)
            .then((res) => {
                if (!res.ok) {
                    console.log(res.status, res.message);
                    return;
                }

                setSelectedQuestId("");
                setQuests((p) => p.filter((q) => q.id !== questId));
                setLocation("/questlog");
            })
            .catch((err) => console.error("error deleting quest:", err));
    };

    const completeQuest = (questId: string) => {
        put<{ dateFinished: string }>(`/quest/${questId}/complete`, {})
            .then((res) => {
                if (!res.ok) {
                    console.log(res.status, res.message);
                    return;
                }

                setSelectedQuestId("");
                setQuests((p) =>
                    p.map((quest) =>
                        quest.id === questId
                            ? { ...quest, dateFinished: res.body.dateFinished }
                            : quest,
                    ),
                );
            })
            .catch((err) => console.error("error completing quest:", err));
    };

    const _quests = quests.reduce(
        (acc, curr) => {
            if (curr.dateFinished === "") {
                acc.incomplete.push(curr);
            } else {
                acc.complete.push(curr);
            }

            return acc;
        },
        { incomplete: [], complete: [] } as {
            incomplete: Array<Quest>;
            complete: Array<Quest>;
        },
    );

    return (
        <div className="routeParent">
            <div className="routeInnerCol">
                <PlatformHeader />

                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        flex: 1,
                        flexDirection: "row",
                    }}
                >
                    <div
                        style={{
                            flexDirection: "column",
                            width: "256px",
                            borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                    >
                        {_quests.incomplete
                            .sort((a, b) => b.difficulty - a.difficulty)
                            .map((q) => (
                                <LogEntry
                                    key={q.id}
                                    id={q.id}
                                    title={q.title}
                                    difficulty={q.difficulty}
                                    active={selectedQuestId === q.id}
                                    onClick={() => {
                                        setSelectedQuestId(q.id);
                                        setCreatingNew(false);
                                    }}
                                />
                            ))}

                        <div className="newQuest">
                            <button
                                className={creatingNew ? "active" : ""}
                                type="button"
                                onClick={() => {
                                    setSelectedQuestId("");
                                    setLocation("/questlog/add");
                                    setCreatingNew(true);
                                }}
                            >
                                +
                            </button>
                        </div>

                        {_quests.complete
                            .sort((a, b) =>
                                new Date(b.dateFinished) <
                                new Date(a.dateFinished)
                                    ? -1
                                    : 1,
                            )
                            .map((q) => (
                                <LogEntry
                                    key={q.id}
                                    id={q.id}
                                    title={q.title}
                                    difficulty={q.difficulty}
                                    active={selectedQuestId === q.id}
                                    complete
                                    onClick={() => {
                                        setSelectedQuestId(q.id);
                                        setCreatingNew(false);
                                    }}
                                />
                            ))}
                    </div>

                    <div style={{ flex: 1 }}>
                        {selectedQuestId !== "" && (
                            <QuestView
                                key={selectedQuestId}
                                quest={
                                    quests.find(
                                        (q) => q.id === selectedQuestId,
                                    ) ?? quests[0]
                                }
                                deleteQuest={() => deleteQuest(selectedQuestId)}
                                updateQuest={(updates) =>
                                    updateQuest(selectedQuestId, updates)
                                }
                                addObjective={(x) =>
                                    addObjective(selectedQuestId, x)
                                }
                                addBattle={(x) => addBattle(selectedQuestId, x)}
                                removeBattle={(x) =>
                                    removeBattle(selectedQuestId, x)
                                }
                                completeQuest={() => {
                                    completeQuest(selectedQuestId);
                                }}
                            />
                        )}
                        {creatingNew && <NewQuest addQuest={addQuest} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuestLog;
